import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  Alert,
  CircularProgress,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CopyIcon from '@mui/icons-material/ContentCopy';
import { useQuery } from '@apollo/client';
import { GET_JOIN_SUGGESTIONS } from 'src/utils/gql/queries';

interface JoinSuggestionsProps {
  connectionId: string;
  mainTable: string;
  schema: string;
  selectedSplits: string[];
  selectedMetrics: string[];
  onGenerateReport: (query: string) => void;
  onSplitsChange?: (splits: string[]) => void;
  onMetricsChange?: (metrics: string[]) => void;
  onSaveAndContinue?: () => void;
}

export const JoinSuggestions: React.FC<JoinSuggestionsProps> = ({
  connectionId,
  mainTable,
  schema,
  selectedSplits,
  selectedMetrics,
  onGenerateReport,
  onSplitsChange,
  onMetricsChange,
  onSaveAndContinue,
}) => {
  const [customQuery, setCustomQuery] = useState<string>('');
  const [copied, setCopied] = useState(false);

  const { data, loading, error } = useQuery(GET_JOIN_SUGGESTIONS, {
    variables: { connectionId, mainTable, schema },
  });

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        Errore nel caricamento dei suggerimenti: {error.message}
      </Alert>
    );
  }

  const suggestion = data?.getJoinSuggestions;

  const generateCustomReportQuery = () => {
    let query = `SELECT `;

    // Aggiungi splits
    const splits = selectedSplits.map((split) => {
      const parts = split.split('.');
      return parts.length > 1 ? `${parts[0]}.${parts[1]}` : split;
    });

    // Aggiungi metrics con aggregazioni
    const metrics = selectedMetrics.map((metric) => {
      const parts = metric.split('.');
      const fieldName = parts.length > 1 ? `${parts[0]}.${parts[1]}` : metric;
      return `SUM(${fieldName}) as total_${parts[1] || metric}`;
    });

    const allFields = [...splits, ...metrics];
    query += allFields.join(', ') + '\n';

    // FROM clause
    query += `FROM ${suggestion.mainTable}\n`;

    // JOIN clauses
    suggestion.joins.forEach((join: any) => {
      query += `${join.type} JOIN ${join.table} ON ${join.on}\n`;
    });

    // GROUP BY per splits
    if (splits.length > 0) {
      query += `GROUP BY ${splits.join(', ')}\n`;
    }

    // ORDER BY per la prima metrica
    if (metrics.length > 0) {
      const firstMetric = metrics[0].split(' as ')[1];
      query += `ORDER BY ${firstMetric} DESC\n`;
    }

    query += `LIMIT 1000;`;

    setCustomQuery(query);
    onGenerateReport(query);
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(customQuery);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Gestione click sui suggerimenti
  const handleSplitSuggestionClick = (split: string) => {
    if (!onSplitsChange) return;

    const newSplits = selectedSplits.includes(split)
      ? selectedSplits.filter((s) => s !== split)
      : [...selectedSplits, split];

    onSplitsChange(newSplits);
  };

  const handleMetricSuggestionClick = (metric: string) => {
    if (!onMetricsChange) return;

    const newMetrics = selectedMetrics.includes(metric)
      ? selectedMetrics.filter((m) => m !== metric)
      : [...selectedMetrics, metric];

    onMetricsChange(newMetrics);
  };

  const handleSelectAllSplits = () => {
    if (!onSplitsChange) return;
    const allSplits = [...selectedSplits, ...suggestion.suggestedSplits];
    const uniqueSplits = allSplits.filter(
      (item, index) => allSplits.indexOf(item) === index,
    );
    onSplitsChange(uniqueSplits);
  };

  const handleSelectAllMetrics = () => {
    if (!onMetricsChange) return;
    const allMetrics = [...selectedMetrics, ...suggestion.suggestedMetrics];
    const uniqueMetrics = allMetrics.filter(
      (item, index) => allMetrics.indexOf(item) === index,
    );
    onMetricsChange(uniqueMetrics);
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        🔗 Suggerimenti di Join e Custom Report
      </Typography>

      <Typography
        variant="body2"
        color="text.secondary"
        gutterBottom
        sx={{ mb: 3 }}
      >
        Tabella principale: <strong>{mainTable}</strong> •{' '}
        {suggestion.joins.length} join suggeriti
      </Typography>

      {/* Relazioni suggerite */}
      <Accordion defaultExpanded sx={{ mb: 3 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              label={`${suggestion.joins.length}`}
              color="primary"
              size="small"
            />
            <Typography variant="h6">Relazioni Trovate</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {suggestion.joins.length > 0 ? (
            suggestion.joins.map((join: any, index: number) => (
              <Paper
                key={index}
                sx={{ p: 2, mb: 2, backgroundColor: '#f8f9fa' }}
              >
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}
                >
                  <Chip label={join.type} color="secondary" size="small" />
                  <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                    {join.table}
                  </Typography>
                </Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontFamily: 'monospace', mb: 1 }}
                >
                  {join.on}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {join.description}
                </Typography>
              </Paper>
            ))
          ) : (
            <Typography variant="body2" color="text.secondary">
              Nessuna relazione trovata per questa tabella
            </Typography>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Campi suggeriti */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <Paper sx={{ p: 2, flex: 1 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
            }}
          >
            <Typography variant="subtitle1">📊 Splits Suggeriti</Typography>
            {onSplitsChange && (
              <Button
                size="small"
                onClick={handleSelectAllSplits}
                variant="outlined"
              >
                Seleziona Tutti
              </Button>
            )}
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {suggestion.suggestedSplits.map((split: string) => (
              <Chip
                key={split}
                label={split}
                size="small"
                color={selectedSplits.includes(split) ? 'primary' : 'default'}
                variant={selectedSplits.includes(split) ? 'filled' : 'outlined'}
                onClick={
                  onSplitsChange
                    ? () => handleSplitSuggestionClick(split)
                    : undefined
                }
                sx={{
                  cursor: onSplitsChange ? 'pointer' : 'default',
                  '&:hover': onSplitsChange
                    ? {
                        backgroundColor: selectedSplits.includes(split)
                          ? 'primary.dark'
                          : 'primary.light',
                        color: selectedSplits.includes(split)
                          ? 'white'
                          : 'primary.contrastText',
                      }
                    : {},
                }}
              />
            ))}
          </Box>
        </Paper>

        <Paper sx={{ p: 2, flex: 1 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
            }}
          >
            <Typography variant="subtitle1">📈 Metrics Suggeriti</Typography>
            {onMetricsChange && (
              <Button
                size="small"
                onClick={handleSelectAllMetrics}
                variant="outlined"
              >
                Seleziona Tutti
              </Button>
            )}
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {suggestion.suggestedMetrics.map((metric: string) => (
              <Chip
                key={metric}
                label={metric}
                size="small"
                color={selectedMetrics.includes(metric) ? 'primary' : 'default'}
                variant={
                  selectedMetrics.includes(metric) ? 'filled' : 'outlined'
                }
                onClick={
                  onMetricsChange
                    ? () => handleMetricSuggestionClick(metric)
                    : undefined
                }
                sx={{
                  cursor: onMetricsChange ? 'pointer' : 'default',
                  '&:hover': onMetricsChange
                    ? {
                        backgroundColor: selectedMetrics.includes(metric)
                          ? 'primary.dark'
                          : 'primary.light',
                        color: selectedMetrics.includes(metric)
                          ? 'white'
                          : 'primary.contrastText',
                      }
                    : {},
                }}
              />
            ))}
          </Box>
        </Paper>
      </Box>

      {/* Generazione Custom Report */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          🎯 Genera Custom Report
        </Typography>

        <Typography variant="body2" color="text.secondary" gutterBottom>
          Selezionati: {selectedSplits.length} splits, {selectedMetrics.length}{' '}
          metrics
        </Typography>

        <Button
          variant="contained"
          color="primary"
          onClick={generateCustomReportQuery}
          disabled={selectedSplits.length === 0 && selectedMetrics.length === 0}
          sx={{ mb: 2 }}
        >
          Genera Query SQL
        </Button>

        {customQuery && (
          <Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 1,
              }}
            >
              <Typography variant="subtitle2">Query SQL Generata:</Typography>
              <Button
                size="small"
                startIcon={<CopyIcon />}
                onClick={copyToClipboard}
                variant="outlined"
              >
                {copied ? 'Copiato!' : 'Copia'}
              </Button>
            </Box>

            <TextField
              multiline
              rows={8}
              value={customQuery}
              onChange={(e) => setCustomQuery(e.target.value)}
              fullWidth
              variant="outlined"
              sx={{
                '& .MuiInputBase-root': {
                  fontFamily: 'monospace',
                  fontSize: '0.875rem',
                },
              }}
            />
          </Box>
        )}
      </Paper>

      {/* Esempio di output */}
      <Paper sx={{ p: 3, backgroundColor: '#f0f8ff' }}>
        <Typography variant="h6" gutterBottom>
          📋 Esempio di Output (tipo Custom Report)
        </Typography>

        <Typography variant="body2" color="text.secondary" gutterBottom>
          Con questa configurazione otterrai un report che mostra:
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {selectedSplits.slice(0, 3).map((split, index) => (
            <Chip
              key={index}
              label={`Split: ${split}`}
              color="primary"
              variant="outlined"
            />
          ))}
          {selectedMetrics.slice(0, 3).map((metric, index) => (
            <Chip
              key={index}
              label={`Metric: ${metric}`}
              color="secondary"
              variant="outlined"
            />
          ))}
        </Box>

        <Typography variant="body2" sx={{ mt: 2, fontStyle: 'italic' }}>
          Perfetto per analisi tipo: "Mostra il costo totale per utente" o
          "Confronta l'uso di token per azienda"
        </Typography>
      </Paper>

      {/* Nessun pulsante di salvataggio qui: la pagina principale gestisce la navigazione e il salvataggio */}
    </Box>
  );
};
